import React from "react";
import { ArrowRight, Megaphone, Layout, Video, Mail } from "lucide-react";
import { SiFacebook, SiInstagram, SiTiktok } from "react-icons/si";

const HeroSection = () => {
  // Sample cards data for the preview mockup
  const previewCards = [
    { 
      title: "Nike - Just Do It Campaign", 
      type: "Ad", 
      typeColor: "text-blue-400",
      platform: "Facebook",
      PlatformIcon: SiFacebook,
      platformColor: "text-blue-500",
      gradient: "from-red-500/40 to-orange-500/20",
      height: "h-44"
    },
    { 
      title: "Stripe Landing Page", 
      type: "Landing", 
      typeColor: "text-emerald-400",
      platform: "Web",
      PlatformIcon: Layout,
      platformColor: "text-gray-400",
      gradient: "from-indigo-500/40 to-purple-500/20",
      height: "h-36"
    },
    { 
      title: "UGC Review - Athletic Greens", 
      type: "UGC", 
      typeColor: "text-purple-400",
      platform: "TikTok",
      PlatformIcon: SiTiktok,
      platformColor: "text-white",
      gradient: "from-emerald-500/40 to-teal-500/20",
      height: "h-52"
    },
    { 
      title: "Notion Email Onboarding", 
      type: "Email", 
      typeColor: "text-amber-400",
      platform: "Email",
      PlatformIcon: Mail,
      platformColor: "text-amber-400",
      gradient: "from-amber-500/30 to-yellow-500/15",
      height: "h-40"
    },
    { 
      title: "Gymshark Carousel", 
      type: "Ad", 
      typeColor: "text-blue-400",
      platform: "Instagram",
      PlatformIcon: SiInstagram,
      platformColor: "text-pink-500",
      gradient: "from-pink-500/40 to-rose-500/20",
      height: "h-48"
    },
    { 
      title: "Linear Homepage", 
      type: "Landing", 
      typeColor: "text-emerald-400",
      platform: "Web",
      PlatformIcon: Layout,
      platformColor: "text-gray-400",
      gradient: "from-blue-500/40 to-cyan-500/20",
      height: "h-36"
    },
  ];

  return (
    <section className="relative pt-32 pb-24 overflow-hidden">
      {/* Atmospheric background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-[radial-gradient(ellipse_at_center,_rgba(120,80,200,0.12)_0%,_rgba(80,50,180,0.06)_30%,_transparent_70%)]" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-1.5 backdrop-blur-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[12px] text-[#8a8a8a]">Utilisé par +10,000 créatifs</span>
          </div>
        </div>

        {/* Main Heading */}
        <div className="text-center max-w-[820px] mx-auto mb-7">
          <h1 className="text-[3rem] md:text-[4.2rem] leading-[1.05] font-bold text-white tracking-[-0.03em] mb-0">
            Votre bibliothèque
            <br />
            <span className="bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent">
              d'inspiration créative.
            </span>
          </h1>
        </div>

        <div className="text-center max-w-[600px] mx-auto mb-10">
          <p className="text-[#7a7a7a] text-base md:text-[1.1rem] leading-relaxed">
            Sauvegardez vos meilleures publicités, landing pages et UGC.
            <br className="hidden md:block" />
            Organisez. Retrouvez. Inspirez-vous en secondes.
          </p>
        </div>

        {/* CTAs */}
        <div className="flex items-center justify-center gap-4 mb-20">
          <a
            href="/login"
            className="inline-flex items-center gap-2.5 bg-white text-black font-medium text-sm rounded-full px-7 py-3 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.15)] hover:bg-white/95 group"
          >
            Commencer gratuitement
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </div>

        {/* Hero Mockup - Platform Preview */}
        <div className="relative max-w-[1000px] mx-auto">
          <div className="absolute -inset-4 bg-gradient-to-b from-purple-500/[0.07] via-transparent to-transparent rounded-3xl blur-2xl" />
          <div className="relative rounded-2xl border border-white/[0.08] bg-[#0c0c0e] overflow-hidden shadow-2xl">
            {/* Browser chrome */}
            <div className="bg-[#111114] border-b border-white/[0.06] px-5 py-3 flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="bg-[#1a1a1e] rounded-lg px-4 py-1 text-[11px] text-[#555] font-mono">app.allsave.co/library</div>
              </div>
            </div>
            
            <div className="p-5">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="bg-white/[0.06] rounded-lg px-3 py-1.5 text-[11px] text-white/60">All Content</div>
                  <div className="bg-transparent rounded-lg px-3 py-1.5 text-[11px] text-white/30">Ads</div>
                  <div className="bg-transparent rounded-lg px-3 py-1.5 text-[11px] text-white/30">Landing Pages</div>
                  <div className="bg-transparent rounded-lg px-3 py-1.5 text-[11px] text-white/30">UGC</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-white/[0.04] rounded-lg px-4 py-1.5 text-[11px] text-white/30 border border-white/[0.06] w-40">Search...</div>
                  <div className="bg-white rounded-lg px-3 py-1.5 text-[11px] text-black font-medium">+ Ajouter</div>
                </div>
              </div>
              
              {/* Masonry Grid Preview */}
              <div className="columns-3 gap-3 space-y-3">
                {previewCards.map((card, i) => (
                  <div
                    key={i}
                    className={`break-inside-avoid bg-[#0a0a0c] rounded-xl border border-white/[0.06] overflow-hidden`}
                  >
                    {/* Card header */}
                    <div className="px-3 pt-3 pb-2">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="text-[11px] text-white/80 font-medium line-clamp-1">{card.title}</span>
                        <div className="flex items-center gap-1">
                          <div className={`w-5 h-5 rounded bg-white/[0.05] flex items-center justify-center ${card.typeColor}`}>
                            {card.type === "Ad" && <Megaphone size={10} />}
                            {card.type === "Landing" && <Layout size={10} />}
                            {card.type === "UGC" && <Video size={10} />}
                            {card.type === "Email" && <Mail size={10} />}
                          </div>
                          <div className={`w-5 h-5 rounded bg-white/[0.05] flex items-center justify-center ${card.platformColor}`}>
                            <card.PlatformIcon size={10} />
                          </div>
                        </div>
                      </div>
                      {/* Action buttons */}
                      <div className="flex items-center gap-1">
                        {[1,2,3,4].map(j => (
                          <div key={j} className="w-6 h-6 rounded bg-white/[0.04] border border-white/[0.06]" />
                        ))}
                      </div>
                    </div>
                    {/* Content area */}
                    <div className={`${card.height} bg-gradient-to-br ${card.gradient}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
